import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Maximize2, Minimize2, Settings2, X, Wrench, CheckCircle2, Play, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Workshop Wall Display — full-screen dark board designed to live on a TV in
// the tech bay. One big card per physical bay, a NEXT UP queue, a TECH DONE
// strip for advisors, and a READY rail. Polls every 15s; clock ticks live.

function stageOf(job) {
  if (job.completed_at) return "collected";
  if (job.ready_for_collection_at) return "ready";
  if (job.workshop_complete_at) return "workshop_complete";
  if (job.in_bay_at) return "in_bay";
  return "waiting";
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function etaOf(job) {
  if (!job.in_bay_at) return null;
  const workMins = job?.schedule_breakdown?.work_mins || job?.estimated_duration_mins || 60;
  return new Date(new Date(job.in_bay_at).getTime() + workMins * 60_000);
}

// Steve's Aug 2026 ask: surface the work type on every wall row. Diary
// imports use `work_types` (structured) but walk-ins often only have a
// free-text `booked_service_name` or `work_description`. Prefer the
// structured list so we can format it consistently (Title Case, `+` joiner).
const WORK_TYPE_SHORT = {
  small_service: "Service",
  large_service: "Large service",
  mot: "MOT",
  brakes: "Brakes",
  diagnostic: "Diagnostic",
  tyres: "Tyres",
  aircon: "Air-con",
  wheel_alignment: "Alignment",
  cambelt: "Cambelt",
  clutch: "Clutch",
  battery: "Battery",
  wipers: "Wipers",
  bulbs: "Bulbs",
  recall: "Recall",
};

function workLabel(job) {
  const types = (job.work_types || [])
    .map((t) => WORK_TYPE_SHORT[String(t || "").toLowerCase()] || String(t || "").replace(/_/g, " "))
    .filter(Boolean);
  if (types.length) return types.join(" + ");
  if (job.booked_service_name) return String(job.booked_service_name);
  if (job.service_category && job.service_category !== "service") {
    return job.service_category.charAt(0).toUpperCase() + job.service_category.slice(1);
  }
  if (job.job_type === "diagnostic") return "Diagnostic";
  if (job.work_description) return String(job.work_description).slice(0, 40);
  return "";
}

const URGENCY_TEXT = { red: "text-red-400", amber: "text-amber-400", green: "text-emerald-400", grey: "text-slate-400" };

export default function WorkshopWallPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [bayCount, setBayCount] = useState(4);
  const [now, setNow] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [bayInput, setBayInput] = useState(4);
  const [isFull, setIsFull] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("wall_view") || "jobs");
  const rootRef = useRef(null);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("wall_view", v); };

  const load = async () => {
    try {
      const [jr, br] = await Promise.all([api.get("/jobs"), api.get("/workshops/me/bays")]);
      setJobs(jr.data || []);
      const bc = br.data?.bay_count || 4;
      setBayCount(bc);
      setBayInput((v) => (showSettings ? v : bc));
    } catch { /* poll again next tick */ }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 15_000);
    const tick = setInterval(() => setNow(new Date()), 1_000);
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => { clearInterval(poll); clearInterval(tick); document.removeEventListener("fullscreenchange", onFs); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current?.requestFullscreen?.();
  };

  const saveBays = async () => {
    const n = Math.max(1, Math.min(20, parseInt(bayInput, 10) || 4));
    try {
      await api.put("/workshops/me/bays", { bay_count: n });
      setBayCount(n);
      setShowSettings(false);
    } catch { /* keep panel open */ }
  };

  const board = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const isToday = (j) => (j.appointment_date || j.appointment_at || j.created_at || "").slice(0, 10) === today;
    // Day progress — collected cars flip to status "completed" and leave the
    // live board, but they still count towards "X of Y cars done".
    const todayAll = (jobs || []).filter((j) => j.status !== "rejected" && isToday(j));
    const doneCount = todayAll.filter((j) => j.workshop_complete_at || j.ready_for_collection_at || j.completed_at).length;
    const active = (jobs || []).filter((j) => {
      if (["completed", "rejected"].includes(j.status)) return false;
      return isToday(j);
    });
    const rank = { red: 0, amber: 1, green: 2, grey: 3 };
    // "Next up" queue on the wall mirrors the Workshop Control panel: sort
    // by earliest latest-safe-start-time so whoever must go on a bay next is
    // always the top card.
    const waiting = active.filter((j) => stageOf(j) === "waiting").sort((a, b) => {
      const cmp = (a.latest_start_time || "9999").localeCompare(b.latest_start_time || "9999");
      if (cmp !== 0) return cmp;
      const ra = rank[a.schedule_urgency || "grey"], rb = rank[b.schedule_urgency || "grey"];
      return ra - rb;
    });
    const inBays = active.filter((j) => ["in_bay", "workshop_complete"].includes(stageOf(j)));
    const ready = active.filter((j) => stageOf(j) === "ready");

    // Slot cars into their assigned bays; legacy cars without a bay_number
    // fill the first free slots; anything beyond bayCount overflows.
    const slots = Array.from({ length: bayCount }, () => null);
    const unplaced = [];
    inBays.forEach((j) => {
      const n = j.bay_number;
      if (n >= 1 && n <= bayCount && !slots[n - 1]) slots[n - 1] = j;
      else unplaced.push(j);
    });
    const overflow = [];
    unplaced.forEach((j) => {
      const free = slots.findIndex((s) => !s);
      if (free >= 0) slots[free] = j;
      else overflow.push(j);
    });
    const techDone = inBays.filter((j) => stageOf(j) === "workshop_complete");
    // Full-day board — every live job in the order the customer needs it
    // back (diary OUT time), earliest first; no promise time sinks to the
    // bottom, tie-broken by latest safe start.
    const allJobs = [...active].sort((a, b) => {
      const pa = a.promised_ready_time || "99:99";
      const pb = b.promised_ready_time || "99:99";
      if (pa !== pb) return pa.localeCompare(pb);
      return (a.latest_start_time || "9999").localeCompare(b.latest_start_time || "9999");
    });
    return { waiting, slots, overflow, ready, techDone, allJobs, activeCount: active.length, doneCount, totalToday: todayAll.length };
  }, [jobs, bayCount]);

  const clock = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div ref={rootRef} className="min-h-screen bg-slate-950 text-slate-100 flex flex-col select-none" data-testid="wall-display">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 px-3 sm:px-6 py-3 sm:py-4 border-b border-slate-800 bg-slate-900/60">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Link to="/dashboard" className="text-slate-500 hover:text-slate-300 shrink-0" data-testid="wall-back-link" title="Back to dashboard">
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </Link>
          <div className="min-w-0">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.25em] text-emerald-400">Workshop Wall</div>
            <div className="font-bold text-base sm:text-xl truncate" data-testid="wall-workshop-name">{user?.workshop_name || "Workshop"}</div>
          </div>
        </div>
        <div className="text-center order-3 sm:order-none w-full sm:w-auto">
          <div className="font-mono text-2xl sm:text-3xl lg:text-5xl font-black tabular-nums tracking-tight" data-testid="wall-clock">{clock}</div>
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate-400">{dateStr}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {board.techDone.length > 0 && (
            <span className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-500/20 text-pink-300 text-sm font-bold ring-2 ring-pink-500/40 animate-pulse" data-testid="wall-tech-done-count">
              <Wrench className="w-4 h-4" /> {board.techDone.length} complete — needs SA
            </span>
          )}
          <div className="inline-flex rounded-lg bg-slate-800 p-1" data-testid="wall-view-toggle">
            <button type="button" onClick={() => setViewPersist("jobs")} className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-bold ${view === "jobs" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-slate-200"}`} data-testid="wall-view-jobs">All jobs</button>
            <button type="button" onClick={() => setViewPersist("bays")} className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-bold ${view === "bays" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-slate-200"}`} data-testid="wall-view-bays">Bays</button>
          </div>
          <button type="button" onClick={() => { setBayInput(bayCount); setShowSettings((s) => !s); }} className="p-2 sm:p-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300" data-testid="wall-settings-btn" title="Set number of bays">
            <Settings2 className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button type="button" onClick={toggleFullscreen} className="hidden sm:inline-flex p-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300" data-testid="wall-fullscreen-btn" title="Fullscreen (TV mode)">
            {isFull ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Bay settings popover */}
      {showSettings && (
        <div className="px-6 py-3 bg-slate-900 border-b border-slate-800 flex items-center gap-3" data-testid="wall-settings-panel">
          <span className="text-sm font-bold text-slate-300">Workshop bays:</span>
          <input
            type="number" min={1} max={20} value={bayInput}
            onChange={(e) => setBayInput(e.target.value)}
            className="w-20 h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-slate-100 font-mono font-bold text-center"
            data-testid="wall-bay-count-input"
          />
          <button type="button" onClick={saveBays} className="h-9 px-4 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold" data-testid="wall-bay-count-save">Save</button>
          <button type="button" onClick={() => setShowSettings(false)} className="p-2 text-slate-500 hover:text-slate-300" data-testid="wall-settings-close"><X className="w-4 h-4" /></button>
          <span className="text-xs text-slate-500">Cars are auto-assigned to the next free bay when a tech presses Start.</span>
        </div>
      )}

      {/* Day progress — the whole team sees the day moving */}
      {board.totalToday > 0 && (
        <div className="px-6 py-3 border-b border-slate-800 bg-slate-900/40 flex items-center gap-5" data-testid="wall-day-progress">
          <div className="shrink-0">
            <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500 leading-none mb-1">Day progress</div>
            <div className="font-black text-2xl leading-none whitespace-nowrap" data-testid="wall-day-progress-count">
              <span className={board.doneCount === board.totalToday ? "text-pink-400" : "text-emerald-400"}>{board.doneCount}</span>
              <span className="text-slate-500 font-bold text-lg"> of </span>
              <span>{board.totalToday}</span>
              <span className="text-slate-400 font-bold text-lg"> cars done</span>
            </div>
          </div>
          <div className="flex-1 h-4 rounded-full bg-slate-800 overflow-hidden" data-testid="wall-day-progress-bar">
            <div
              className={`h-full rounded-full transition-[width] duration-700 ${board.doneCount === board.totalToday ? "bg-pink-500" : "bg-gradient-to-r from-emerald-600 to-emerald-400"}`}
              style={{ width: `${Math.round((board.doneCount / board.totalToday) * 100)}%` }}
            />
          </div>
          <div className={`shrink-0 font-mono font-black text-2xl tabular-nums ${board.doneCount === board.totalToday ? "text-pink-400" : "text-emerald-400"}`} data-testid="wall-day-progress-pct">
            {Math.round((board.doneCount / board.totalToday) * 100)}%
          </div>
        </div>
      )}

      {/* Board */}
      <main className="flex-1 p-4 md:p-6 overflow-auto">
        {view === "jobs" ? (
          <JobBoard jobs={board.allJobs} now={now} />
        ) : (
          <>
            <div className={`grid gap-4 ${bayCount <= 2 ? "grid-cols-1 md:grid-cols-2" : bayCount <= 4 ? "grid-cols-2 xl:grid-cols-4" : bayCount <= 6 ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-2 lg:grid-cols-4"}`} data-testid="wall-bay-grid">
              {board.slots.map((job, i) => (
                <BayCard key={i} bayNo={i + 1} job={job} now={now} />
              ))}
            </div>
            {board.overflow.length > 0 && (
              <div className="mt-4 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10" data-testid="wall-overflow">
                <div className="text-xs font-black uppercase tracking-widest text-amber-400 mb-2">Over capacity — {board.overflow.length} more in the workshop</div>
                <div className="flex flex-wrap gap-3">
                  {board.overflow.map((j) => (
                    <span key={j.id} className="font-mono font-bold text-lg">{j.vehicle_reg || j.customer_name}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Bottom rails (bays view) + colour legend */}
      <footer className="border-t border-slate-800 bg-slate-900/60 px-6 py-3">
        {view === "bays" && (
          <div className="grid gap-4 md:grid-cols-[2fr_1fr] mb-3">
        <div data-testid="wall-next-up">
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-yellow-400 mb-2">
            <Play className="w-4 h-4" /> Checked in · next up
          </div>
          {board.waiting.length === 0 ? (
            <div className="text-slate-500 text-sm">Queue clear — nothing waiting to start.</div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {board.waiting.slice(0, 8).map((j, idx) => (
                <div key={j.id} className={`shrink-0 rounded-lg border px-4 py-2.5 bg-yellow-400/[0.08] ${idx === 0 && ["red", "amber"].includes(j.schedule_urgency) ? "border-red-500 ring-2 ring-red-500/40 animate-pulse" : "border-yellow-500/60"}`} data-testid={`wall-next-${j.id}`}>
                  <div className="font-mono font-black text-xl leading-tight text-yellow-300">{j.vehicle_reg || "—"}</div>
                  <div className="text-xs text-slate-400 truncate max-w-[160px]">{j.customer_name}</div>
                  <div className={`text-xs font-bold mt-1 ${URGENCY_TEXT[j.schedule_urgency || "grey"]}`}>
                    start by {fmtTime(j.latest_start_time)}{j.promised_ready_time ? ` · out ${j.promised_ready_time}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div data-testid="wall-ready-rail">
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-pink-400 mb-2">
            <CheckCircle2 className="w-4 h-4" /> Ready for collection
          </div>
          {board.ready.length === 0 ? (
            <div className="text-slate-500 text-sm">Nothing waiting for pickup.</div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {board.ready.slice(0, 6).map((j) => (
                <div key={j.id} className="shrink-0 rounded-lg border border-pink-500/60 bg-pink-500/10 px-4 py-2.5" data-testid={`wall-ready-${j.id}`}>
                  <div className="font-mono font-black text-xl leading-tight text-pink-300">{j.vehicle_reg || "—"}</div>
                  <div className="text-xs text-slate-400 truncate max-w-[160px]">{j.customer_name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 pb-[env(safe-area-inset-bottom)]" data-testid="wall-legend">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-yellow-400" /> Checked in</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Tech on job</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-sky-400" /> Waiting parts / authority</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-pink-500" /> Job complete</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500 animate-pulse" /> Overdue</span>
        </div>
      </footer>
    </div>
  );
}

function promiseDate(j, now) {
  if (!j.promised_ready_time) return null;
  // Same separator tolerance as the backend scheduler. Splitting on ":" alone
  // parsed "12.45" as hour 12 with NaN minutes -> 12:00, so rows on a
  // dot-format diary flashed overdue up to an hour early.
  const mt = String(j.promised_ready_time).trim().match(/^(\d{1,2})[.:hH]?(\d{2})$/);
  if (!mt) return null;
  const h = parseInt(mt[1], 10);
  const m = parseInt(mt[2], 10);
  if (h > 23 || m > 59) return null;
  const d = new Date(now);
  d.setHours(h, m || 0, 0, 0);
  return d;
}

// Full-day board — one compact row per job so a 25-job day fits on one TV
// screen. Ordered by the diary's customer OUT time; the tracker (bay, tech,
// hold, complete) lives on every row.
function JobBoard({ jobs, now }) {
  if (jobs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500 text-lg" data-testid="wall-job-board-empty">
        No jobs booked in for today — import a diary and the board fills itself.
      </div>
    );
  }
  const cols = jobs.length > 24 ? 3 : jobs.length > 12 ? 2 : 1;
  const compact = jobs.length > 12;
  const rowsPerCol = Math.ceil(jobs.length / cols);
  return (
    <div
      className={`grid gap-x-4 gap-y-1.5 content-start ${cols === 3 ? "grid-cols-1 lg:grid-cols-3" : cols === 2 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 max-w-6xl mx-auto"}`}
      style={cols > 1 ? { gridAutoFlow: "column", gridTemplateRows: `repeat(${rowsPerCol}, auto)` } : undefined}
      data-testid="wall-job-board"
    >
      {jobs.map((j, i) => (
        <JobBoardRow key={j.id} job={j} idx={i + 1} now={now} compact={compact} />
      ))}
    </div>
  );
}

function JobBoardRow({ job: j, idx, now, compact }) {
  const stage = stageOf(j);
  const hold = ["waiting", "in_bay"].includes(stage) ? (j.on_hold || null) : null;
  const eta = stage === "in_bay" && !hold ? etaOf(j) : null;
  const etaOver = eta && now.getTime() > eta.getTime();
  const pd = promiseDate(j, now);
  const promiseOver = pd && now > pd && !["ready", "workshop_complete", "collected"].includes(stage);
  // Red flash on start-by once we're past the latest safe start and the job
  // still hasn't rolled into a bay — mirrors Mark's "start-by is the order
  // of importance" ask on the wall.
  const startAt = j.latest_start_time ? new Date(j.latest_start_time) : null;
  const startOver = startAt && now > startAt && stage === "waiting" && !hold;

  // Colour code: yellow checked-in / green tech on job / pale blue on hold /
  // pink complete-or-ready / red flash when the promise time has blown.
  const tone = hold
    ? { row: "border-sky-400 bg-sky-400/[0.10]", chip: "bg-sky-400 text-slate-900", out: "text-sky-300" }
    : stage === "workshop_complete" || stage === "ready"
    ? { row: "border-pink-500 bg-pink-500/[0.10]", chip: "bg-pink-500 text-white", out: "text-pink-300" }
    : stage === "in_bay"
    ? { row: "border-emerald-500 bg-emerald-500/[0.09]", chip: "bg-emerald-600 text-white", out: "text-emerald-300" }
    : { row: "border-yellow-500/70 bg-yellow-400/[0.09]", chip: "bg-yellow-400 text-slate-900", out: "text-yellow-300" };

  const statusLabel = hold
    ? hold.reason === "parts" ? "Waiting on parts" : "Waiting authority"
    : stage === "workshop_complete"
    ? "Job complete"
    : stage === "ready"
    ? "Ready to collect"
    : stage === "in_bay"
    ? `In bay${j.bay_number ? ` ${j.bay_number}` : ""}`
    : "Checked in";

  // Tracker line under the reg. For in-bay jobs show tech + start time; for
  // waiting jobs we surface just the assigned tech (or omit) because start-by
  // is now the big primary time on the right — no need to repeat it here.
  const trackerLine = stage === "in_bay" || stage === "workshop_complete"
    ? `${j.assigned_technician_name || "Tech"} · started ${fmtTime(j.in_bay_at)}`
    : stage === "waiting"
    ? (j.assigned_technician_name || null)
    : null;

  return (
    <div
      className={`overflow-hidden rounded-lg border-l-[6px] border ${tone.row} px-3 ${compact ? "py-1" : "py-2"} ${promiseOver ? "ring-2 ring-red-500/60 animate-pulse" : ""}`}
      data-testid={`wall-board-row-${j.id}`}
    >
      {/* Top line — index + reg + status + start-by. Stacks nothing; on
          phone the customer name and tracker drop to line 2 so the
          status pill and start-by can't collide with the reg. */}
      <div className="flex items-center gap-2.5">
        <div className="w-6 sm:w-7 text-right font-mono font-black text-slate-500 tabular-nums shrink-0">{idx}</div>
        <span className={`font-mono font-black ${compact ? "text-lg" : "text-lg sm:text-xl"} leading-tight whitespace-nowrap shrink-0`} data-testid={`wall-board-reg-${j.id}`}>{j.vehicle_reg || "—"}</span>
        <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${tone.chip} whitespace-nowrap shrink-0`} data-testid={`wall-board-status-${j.id}`}>{statusLabel}</span>
        <div className="flex-1 min-w-0" />
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-slate-400 leading-none">Start by</div>
          <div className={`font-mono font-black ${compact ? "text-lg" : "text-lg sm:text-xl"} tabular-nums leading-tight ${startOver ? "text-red-400" : "text-slate-100"}`} data-testid={`wall-board-startby-${j.id}`}>
            {j.latest_start_time ? fmtTime(j.latest_start_time) : "—"}
          </div>
          <div className={`text-[10px] font-mono font-bold tabular-nums leading-tight mt-0.5 ${promiseOver ? "text-red-400" : "text-slate-500"}`} data-testid={`wall-board-out-${j.id}`}>
            out {j.promised_ready_time || "—"}
          </div>
        </div>
      </div>
      {/* Bottom line — customer + work + tracker. flex-wrap so long names
          don't push the status pill on phones. */}
      <div className="mt-1 pl-8 sm:pl-9 flex items-center gap-2 flex-wrap min-w-0">
        <span className="text-xs sm:text-sm text-slate-300 truncate min-w-0 flex-1">{j.customer_name}</span>
        {workLabel(j) && (
          <span
            className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-100 border border-slate-500/40 shrink-0"
            data-testid={`wall-board-work-${j.id}`}
            title="Work booked in"
          >
            {workLabel(j)}
          </span>
        )}
        {(trackerLine || eta) && (
          <div className="text-[11px] text-slate-400 truncate leading-tight shrink-0" data-testid={`wall-board-tracker-${j.id}`}>
            {trackerLine}
            {eta && (
              <span className={`font-bold ${etaOver ? "text-red-400" : "text-emerald-300"}`}>
                {trackerLine ? " · " : ""}ETA {fmtTime(eta.toISOString())}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BayCard({ bayNo, job, now }) {
  if (!job) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/40 min-h-[210px] flex flex-col items-center justify-center gap-1" data-testid={`wall-bay-free-${bayNo}`}>
        <div className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-600">Bay {bayNo}</div>
        <div className="text-2xl font-black text-slate-700">FREE</div>
      </div>
    );
  }
  const techDone = !!job.workshop_complete_at;
  const hold = !techDone ? (job.on_hold || null) : null;
  const eta = etaOf(job);
  const msLeft = eta ? eta.getTime() - now.getTime() : null;
  const overdue = !techDone && !hold && msLeft !== null && msLeft < 0;
  const minsAbs = msLeft !== null ? Math.round(Math.abs(msLeft) / 60_000) : null;

  // Colour code: green = tech working, pale blue = waiting on parts /
  // authority, pink = job complete, red flash = overdue.
  const shell = techDone
    ? "border-pink-500 bg-pink-500/10"
    : hold
    ? "border-sky-400 bg-sky-400/10"
    : overdue
    ? "border-red-500 bg-red-500/10 animate-pulse"
    : "border-emerald-500 bg-emerald-500/[0.08]";

  return (
    <div className={`rounded-2xl border-2 ${shell} min-h-[210px] p-4 flex flex-col`} data-testid={`wall-bay-card-${bayNo}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-500">Bay {bayNo}</span>
        {techDone ? (
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-pink-500 text-white inline-flex items-center gap-1"><Wrench className="w-3 h-3" /> Job complete — needs SA</span>
        ) : hold ? (
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-sky-400 text-slate-900" data-testid={`wall-hold-${job.id}`}>
            {hold.reason === "parts" ? "Waiting on parts" : "Waiting authority"}
          </span>
        ) : overdue ? (
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-red-600 text-white" data-testid={`wall-overdue-${job.id}`}>Overdue {minsAbs} min</span>
        ) : (
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded bg-emerald-600/80 text-white">In progress</span>
        )}
      </div>
      <div className="mt-2 font-mono font-black text-4xl tracking-tight leading-none" data-testid={`wall-bay-reg-${bayNo}`}>{job.vehicle_reg || "—"}</div>
      <div className="text-slate-300 font-medium truncate mt-1">{job.customer_name}</div>
      <div className="text-xs text-slate-300 truncate font-semibold" data-testid={`wall-bay-work-${bayNo}`}>{workLabel(job) || "—"}</div>
      <div className="mt-auto pt-3 flex items-end justify-between gap-2">
        <div className="text-sm text-slate-400 min-w-0">
          <div className="truncate font-semibold text-slate-200">{job.assigned_technician_name || "Tech"}</div>
          <div className="text-xs">started {fmtTime(job.in_bay_at)}{job.promised_ready_time ? ` · out ${job.promised_ready_time}` : ""}</div>
        </div>
        {!techDone && !hold && eta && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{overdue ? "ETA passed" : "ETA"}</div>
            <div className={`font-mono font-black text-2xl tabular-nums ${overdue ? "text-red-400" : "text-emerald-300"}`} data-testid={`wall-bay-eta-${bayNo}`}>
              {fmtTime(eta.toISOString())}
            </div>
            {!overdue && minsAbs !== null && <div className="text-xs text-slate-400">{minsAbs} min left</div>}
          </div>
        )}
      </div>
    </div>
  );
}
